import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { STATUS, SWAL_SETTINGS, THEME_COLORS } from "../../../../../utils/Constants";
import {
  TrimText,
  formateDateWithMonth,
  handleServerValidations,
  hasP,
} from "../../../../../utils/commonfunction";
import * as players from "../../../services/players.service";
import * as ClubManagamentApi from "../../../services/club.service";
import * as clubAdmin from "../../../services/clubAdmin.service";
import ActionCell from "../../../shared/ActionCell";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import moment from "moment";

ModuleRegistry.registerModules([AllCommunityModule]);

// Manual "Charge Current Month" / "Charge Next Month" actions are hidden; billing runs via the monthly cron
const SHOW_MANUAL_CHARGE_BUTTONS = false;

const ClubManagament = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();

  const [searchData, setSearchData] = useState("");
  const [statsupdate, setStatusUpdate] = useState("false");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("verified");
  const [selectedRows, setSelectedRows] = useState([]);

  const onSelectionChanged = (event) => {
    if (event && event.api) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedRows(selectedData);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedRows.length === 0) return;
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while selected records are being deleted",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const deletePromises = selectedRows.map((row) => clubAdmin.Delete(row.originalId));
        Promise.all(deletePromises)
          .then(() => {
            setSelectedRows([]);
            if (gridRef.current && gridRef.current.api) {
              gridRef.current.api.deselectAll();
            }
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: `${selectedRows.length} record(s) deleted successfully.`,
              ...SWAL_SETTINGS,
            });
            refetch();
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  const [actionState, setActionState] = useState({
    id: null,
    shouldVisible: false,
    positions: { top: 0, right: 0 },
    rowData: {}
  });

  const [actionOptions, setActionOptions] = useState([]);

  function Verifyfunction(data, verifyStatus) {
    const isVerifying = verifyStatus === "verified" || verifyStatus === true || verifyStatus === "true";
    const htmlContent = isVerifying ? `
      <div class="text-center mt-2">
        <p class="text-muted text-center mb-0">${t("you_not_be_able_to_revert_this")}</p>
      </div>
    ` : `
      <div class="text-start mt-2">
        <p class="text-muted text-center mb-3">${t("you_not_be_able_to_revert_this")}</p>
        <div class="form-group text-start">
          <label for="swal-reason-textarea" class="form-label font-weight-bold mb-1 text-dark" style="font-size: 14px; font-weight: 600;">
            ${t("Reason")}
          </label>
          <textarea
            id="swal-reason-textarea"
            class="form-control"
            rows="3"
            placeholder="${t("Write reason here...")}"
            style="font-size: 14px; border: 1px solid #ced4da; border-radius: 6px; padding: 8px 12px;"
          ></textarea>
        </div>
      </div>
    `;

    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      html: htmlContent,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel"),
      preConfirm: () => {
        if (isVerifying) return "";
        const textarea = document.getElementById("swal-reason-textarea");
        return textarea ? textarea.value.trim() : "";
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const reason = result.value || "";
        const formdata = new FormData();
        let id = [data.originalId];
        formdata.append("isProfileVerified", verifyStatus);
        formdata.append("reason", reason);
        clubAdmin.profileVerify(formdata, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  function Blockfunction(data, blockStatus) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        const formdata = new FormData();
        formdata.append("isBlocked", blockStatus);
        clubAdmin.blockUser(formdata, data.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  function triggerActions(e, row) {
    const rect = e.currentTarget.getBoundingClientRect();
    const top = rect.top;
    const right = window.innerWidth - rect.right;

    let dynamicOptions = [];

    if (row.isProfileVerified === "verified") {
      if (row.isBlocked) {
        dynamicOptions = [
          {
            label: "Unblock",
            value: false,
            onClick: Blockfunction
          }
        ];
      } else {
        dynamicOptions = [
          {
            label: "Block",
            value: true,
            onClick: Blockfunction
          }
        ];
      }
    } else if (row.isProfileVerified === "rejected") {
      dynamicOptions = [
        {
          label: "Verify",
          value: "verified",
          onClick: Verifyfunction
        }
      ];
    } else {
      dynamicOptions = [
        {
          label: "Reject",
          value: "rejected",
          onClick: Verifyfunction
        },
        {
          label: "Verify",
          value: "verified",
          onClick: Verifyfunction
        }
      ];
    }

    setActionOptions(dynamicOptions);

    setActionState({
      id: "",
      shouldVisible: true,
      positions: { top, right },
      rowData: row
    });
  }

  const renderProfileStatus = (row) => {
    if (row?.isBlocked) {
      return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">Blocked</span>;
    }
    switch (row?.isProfileVerified) {
      case "verified":
        return <span className="badge badge-pill badge-success-light fw-bold text-uppercase">{t("label_verified")}</span>;
      case "rejected":
        return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">{t("label_rejected")}</span>;
      default:
        return <span className="badge badge-pill badge-warning-light fw-bold text-uppercase">{t("label_pending")}</span>;
    }
  };

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Club Management"), url: "" },
  ];

  // ================= FETCH ALL DATA =================
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["players-list"],
    queryFn: () => ClubManagamentApi.List({
      limit: 1000,
      lastKey: null,
      filters: {},
      sorting: {}
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const viewfunction = (row) => {
    navigate(`/admin/club-management/view/${row.originalId}`);
  };

  const handleChargeBilling = (row) => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Charge Billing & Generate Snapshot?",
      text: `Are you sure you want to generate billing snapshot and charge the bill for "${row.name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Charge Bill",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing Billing...",
          text: "Generating snapshot and processing payment via Stripe",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.chargeBilling(row.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Billing Charged!",
              text: response.message || "Snapshot generated and payment charged successfully.",
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const handleChargeAllBilling = () => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Run Manual Billing for All Clubs?",
      text: "This will calculate billing, generate snapshots, and process Stripe charges for all active clubs.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Charge All",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing All Billing...",
          text: "Generating snapshots and charging invoices across active clubs",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.chargeAllBilling().then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Manual Billing Complete",
              text: response.message || "All active club billings processed successfully.",
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const handleChargeNextMonthBilling = (row) => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Charge Next Month Billing & Generate Snapshot?",
      text: `Are you sure you want to generate billing snapshot and charge the Next Month bill for "${row.name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Charge Next Month",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing Next Month Billing...",
          text: "Generating next month snapshot and processing payment via Stripe",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.chargeNextMonthBilling(row.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Next Month Billing Charged!",
              text: response.message || "Next month snapshot generated and payment charged successfully.",
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const handleChargeAllNextMonthBilling = () => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Run Next Month Manual Billing for All Clubs?",
      text: "This will calculate next month billing, generate snapshots, and process Stripe charges for all active clubs.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Charge All Next Month",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing All Next Month Billing...",
          text: "Generating next month snapshots and charging invoices across active clubs",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.chargeAllNextMonthBilling().then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Next Month Billing Complete",
              text: response.message || "All active club next month billings processed successfully.",
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const handleSendPendingInvoiceEmail = (row) => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Send Pending Invoice Email?",
      text: `Are you sure you want to send the 25th pending invoice email preview to "${row.name || row.clubAdminName || 'Club Admin'}" (${row.email})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Send Email",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Sending Email...",
          text: "Generating pending snapshot preview and sending email to club admin",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.sendPendingInvoiceEmail(row.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Email Sent!",
              text: response.message || "Pending invoice email sent successfully to club admin.",
              ...SWAL_SETTINGS,
            });
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: "",
      field: "checkbox",
      maxWidth: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
    },
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },

    {
      headerName: t("name"),
      field: "name",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },

    {
      headerName: t("label_club_school"),
      field: "clubAdmin",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        const clubId = params?.node?.id;
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: clubId ? "pointer" : "default",
            }}
            onClick={() => {
              if (clubId) {
                navigate(`/admin/user-management/club-admins/view/${clubId}`);
              }
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },

    {
      headerName: t("address"),
      field: "address",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },

    {
      headerName: "Stripe Customer ID",
      field: "stripeCustomerId",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.data?.stripeCustomerId || "N/A"
    },

    {
      headerName: "Billing Status",
      field: "billingStatus",
      flex: 1,
      minWidth: 130,
      cellRenderer: (params) => {
        const row = params.data;
        if (row.stripeCustomerId) {
          return <span className="badge badge-pill badge-success fw-bold text-uppercase">Active</span>;
        }
        return (
          <span className="badge badge-pill badge-primary-light fw-bold text-uppercase">
            {row.billingStatus ? row.billingStatus.replace(/_/g, " ") : "N/A"}
          </span>
        );
      }
    },

    {
      headerName: t("label_verification"),
      field: "isProfileVerified",
      minWidth: 140,
      maxWidth: 140,
      sortable: false,
      filter: true,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="d-flex ml-3 align-items-center justify-content-between">
            {renderProfileStatus(row)}
            <Whisper
              placement="top"
              trigger="hover"
              speaker={<Tooltip style={{ fontWeight: "bold" }}>Verification status</Tooltip>}
            >
              <button
                type="button"
                className="btn btn-sm p-0 ms-2"
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "16px"
                }}
                onClick={(e) => triggerActions(e, row)}
              >
                <i className="ri-settings-3-fill"></i>
              </button>
            </Whisper>
          </div>
        );
      }
    },
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? formateDateWithMonth(params.value) : "N/A"
    },

    ...(hasP("club-view") ? [{
      headerName: t("list_heading_action"),
      pinned: "right",
      minWidth: 260,
      maxWidth: 260,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("club-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => viewfunction(row)}
                  >
                    <i className="ri-eye-fill"></i>
                  </button>
                </Whisper>
              )}

              {SHOW_MANUAL_CHARGE_BUTTONS && hasP("club-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Charge Current Month</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    style={{ backgroundColor: '#2563eb', color: '#fff' }}
                    onClick={() => handleChargeBilling(row)}
                  >
                    <i className="ri-bank-card-line"></i>
                  </button>
                </Whisper>
              )}

              {SHOW_MANUAL_CHARGE_BUTTONS && hasP("club-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Charge Next Month</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    style={{ backgroundColor: '#059669', color: '#fff' }}
                    onClick={() => handleChargeNextMonthBilling(row)}
                  >
                    <i className="ri-calendar-check-line"></i>
                  </button>
                </Whisper>
              )}

              {hasP("club-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Send 25th Pending Invoice Email</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    style={{ backgroundColor: '#d97706', color: '#fff' }}
                    onClick={() => handleSendPendingInvoiceEmail(row)}
                  >
                    <i className="ri-mail-send-line"></i>
                  </button>
                </Whisper>
              )}

              {hasP("club-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Billing Snapshot</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => navigate(`/admin/club-management/billing-snapshot/${row.originalId}`)}
                  >
                    <i className="ri-camera-lens-line"></i>
                  </button>
                </Whisper>
              )}

              {hasP("club-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Billing Logs</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => navigate(`/admin/club-management/billing-logs/${row.originalId}`)}
                  >
                    <i className="ri-file-list-3-line"></i>
                  </button>
                </Whisper>
              )}
            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate]);

  // 🔥 FIXED ROW DATA - S.No Dynamic
  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];

    return data.data.list
      .map((row, i) => ({
        srNo: 0,
        originalId: row.id,
        name: row?.clubDetails?.name?.trim() ? row.clubDetails.name.trim() : "N/A",
        clubAdmin: `${row?.clubAdminName}` || "N/A",
        address: row?.clubDetails?.address || 
          [row?.city, row?.country]
            .filter(Boolean)
            .join(", ") || 
          "N/A",
        city: row?.city || "N/A",
        country: row?.country || "N/A",
        location: `${row?.city ? TrimText(row.city, 20) : "N/A"}, ${row?.country ? TrimText(row.country, 20) : "N/A"}`,
        isProfileVerified: row?.isProfileVerified || "pending",
        isBlocked: row?.isBlocked || false,
        status: row.status,
        createdAt: row.createdAt,
        stripeCustomerId: row.stripeCustomerId,
        billingStatus: row.billingStatus,
        commercialAgreementSigned: row.commercialAgreementSigned,
        isTermsAndConditionsAccepted: row.isTermsAndConditionsAccepted,
        paymentMethodName: row.paymentMethodName,
      }))
      .filter((row) => {
        // Tab Filtering
        if (activeTab === "verified") {
          if (row.isProfileVerified !== "verified" || row.isBlocked) return false;
        } else if (activeTab === "pending") {
          if (row.isProfileVerified !== "pending" || row.isBlocked) return false;
        } else if (activeTab === "rejected_blocked") {
          if (row.isProfileVerified !== "rejected" && !row.isBlocked) return false;
        }

        // Search Filtering
        return searchData
          ? row.name?.toLowerCase().includes(searchData.toLowerCase()) ||
          row.clubAdmin?.toLowerCase().includes(searchData.toLowerCase())
          : true;
      });
  }, [data, searchData, activeTab]);

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    rowSelection: "multiple",
    onSelectionChanged: onSelectionChanged,
    getRowId: (params) => String(params.data.originalId),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        const currentPage = params.api.paginationGetCurrentPage();
        const pageSize = params.api.paginationGetPageSize();
        setPerPage(pageSize);
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <h6 className="main-content-label mb-0">Club Management</h6>
                {selectedRows.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                    onClick={handleDeleteMultiple}
                  >
                    <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
              </div>
              <div className="d-flex align-items-center">
              
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("input_placeholder_search")}
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                {/* date */}
                <div className="me-3">
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef.current || !gridRef.current.api) return;

                        if (!value || value.length !== 2) {
                          gridRef.current.api.setFilterModel(null);
                          gridRef.current.api.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef.current.api.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss"),
                          }
                        });
                        gridRef.current.api.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>
                {/* {hasP("club-view") && (
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                      onClick={handleChargeAllBilling}
                    >
                      <i className="ri-bank-card-line"></i> Charge All (Current)
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success d-flex align-items-center gap-1"
                      style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                      onClick={handleChargeAllNextMonthBilling}
                    >
                      <i className="ri-calendar-check-line"></i> Charge All (Next Month)
                    </button>
                  </div>
                )} */}
              </div>
            </div>

            {isLoading || isFetching ? (
              <Placeholder.Paragraph
                className="w-100 admin-placeholder"
                rowHeight={30}
                rowSpacing={5}
                active={true}
                rows={7}
              />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
      <ActionCell data={actionState} onClose={() => setActionState(null)} options={actionOptions} />
    </>
  );
};

export default ClubManagament;